/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { SmartphoneIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useParams, useSearchParams } from "react-router"

import type { PageApplicationItem } from "../../features/application/application-types"
import { ApplicationStatus } from "../../features/application/application-types"
import {
    getAppTypeIdFromParams,
    getStatusFromUrl,
    updateApplicationUrlSearchParams
} from "../../features/application/application-url-utils"
import { ApplicationViewModel } from "../../features/application/application-viewmodel"
import { capitalize } from "../../utils/capitalize"
import { useViewModel } from "../app/app-hooks"
import { ApplicationBreadcrumb } from "../components/application-breadcrumb"
import { ApplicationSearch } from "../components/application-search"
import { PaginationControls } from "../components/pagination-controls"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"
import { ApplicationListFlagFilter } from "./application-list-flag-filter"
import { ApplicationListItem } from "./application-list-item"

import styles from "./application-list-view.module.css"

export const ApplicationListView = observer(() => {
    const vm = useViewModel(ApplicationViewModel)
    const appTypeId = getAppTypeIdFromParams(useParams())

    const [searchParams] = useSearchParams()
    const status = getStatusFromUrl(searchParams)

    const handleFlagsAndUrl = async (flags: string[]) => {
        if (!appTypeId || !status) return

        updateApplicationUrlSearchParams(flags, 0)

        await vm.getApplications(appTypeId, status, flags, 0)
    }

    const handlePageChange = async (pageIndex: number) => {
        if (!appTypeId || !status) return

        const currentFlags = vm.selectedPageId?.flags ?? []
        updateApplicationUrlSearchParams(currentFlags, pageIndex)

        await vm.getApplications(appTypeId, status, currentFlags, pageIndex)
    }

    return (
        <div className="w-content max-inline-[2400px]">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-y-4">
                <div className="grid gap-2">
                    <h1 className="m-0 text-3xl font-bold text-primary">
                        {`${capitalize(searchParams.get("status"))} Devices`}
                    </h1>
                    <ApplicationBreadcrumb selectedPageId={vm.selectedPageId} />
                </div>
                <ApplicationSearch />
            </header>

            {status === ApplicationStatus.insecure && (
                <ApplicationListFlagFilter
                    selectedFlags={vm.selectedPageId?.flags ?? []}
                    onFlagsChange={handleFlagsAndUrl}
                />
            )}

            {vm.selectedPageItem?.content?.length === 0 && (
                <Empty className="border bg-background">
                    <EmptyHeader>
                        <EmptyMedia>
                            <SmartphoneIcon />
                        </EmptyMedia>
                        <EmptyTitle>No devices found</EmptyTitle>
                        {!!vm.selectedPageId?.flags.length && (
                            <EmptyDescription>Nothing matches the selected flags. Try removing one.</EmptyDescription>
                        )}
                    </EmptyHeader>
                </Empty>
            )}

            {vm.selectedPageId && !!vm.selectedPageItem?.content?.length && (
                <div className={styles.list}>
                    {vm.selectedPageItem.content.map((item: PageApplicationItem) => (
                        <ApplicationListItem key={item.avUserId} item={item} />
                    ))}
                </div>
            )}

            {vm.selectedPageId && (
                <PaginationControls
                    currentPage={vm.selectedPageItem?.number ?? 0}
                    totalPages={vm.selectedPageItem?.totalPages ?? 0}
                    onPageChange={handlePageChange}
                    className="mt-6"
                />
            )}
        </div>
    )
})
